import { useEffect, useRef, useState } from "react";
import type { ProjectBrief } from "../../contracts/types";

interface ChatMsg {
  role: "user" | "assistant";
  text: string;
  applied?: string[]; // friendly names of sections changed
}

// Map brief keys to friendly section names for the "changed" summary.
const FIELD_LABEL: Record<string, string> = {
  projectName: "Project name",
  projectLocation: "Location",
  worksDescription: "Description of the Works",
  contractReference: "Contract reference",
  employer: "Employer",
  contractor: "Contractor",
  engineer: "Engineer",
  contractPrice: "Contract price",
  contractPriceWords: "Contract price in words",
  timeForCompletion: "Time for completion",
  defectsNotification: "Defects period",
  delayDamagesPerDay: "Delay damages",
  delayDamagesCapPercent: "Delay damages cap",
  performanceSecurityPercent: "Performance security",
  advancePaymentPercent: "Advance payment",
  advanceRepayment: "Advance repayment",
  retentionPercent: "Retention",
  retentionCapPercent: "Retention cap",
  minimumIpc: "Minimum IPC",
  paymentPeriodDays: "Payment period",
  governingLaw: "Governing law",
  rulingLanguage: "Ruling language",
  disputeResolution: "Dispute resolution",
  daab: "Dispute board",
  narrative: "Special conditions — narrative",
  specialProvisions: "Special conditions — clause amendments",
  specialConditionsAdd: "Special Conditions of Contract",
  specialConditionsEdit: "Special Conditions (rewritten)",
  specialConditionsBlocks: "Special Conditions of Contract",
  advanceSchedule: "Advance payment schedule",
  advanceInstallments: "Advance installments",
  drawings: "Drawing register",
  specifications: "Specifications",
};
const labelFor = (k: string) => FIELD_LABEL[k] || k;

const SUGGEST = "Review the contract and suggest 3–5 specific, high-value improvements. List them briefly and numbered; don't change anything yet.";

export function AssistantChat({ brief, onPatch, aiReady }: { brief: ProjectBrief; onPatch: (patch: Partial<ProjectBrief>) => void; aiReady: boolean | null }) {
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [msgs, busy]);

  async function send(text: string) {
    const t = text.trim();
    if (!t || busy) return;
    setErr(null);
    const next = [...msgs, { role: "user" as const, text: t }];
    setMsgs(next);
    setInput("");
    setBusy(true);
    try {
      const res = await fetch("/api/assist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next.map((m) => ({ role: m.role, text: m.text })), brief }),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const data = (await res.json()) as { reply?: string; patch?: Partial<ProjectBrief> | null };
      let applied: string[] | undefined;
      if (data.patch && Object.keys(data.patch).length) {
        onPatch(data.patch);
        applied = Object.keys(data.patch).map(labelFor);
      }
      setMsgs((m) => [...m, { role: "assistant", text: data.reply || (applied ? "Applied the changes." : "(no reply)"), applied }]);
    } catch (e: any) {
      setErr(`Assistant unavailable (${String(e.message).slice(0, 90)}).`);
      setMsgs((m) => m.slice(0, -1)); // drop the user msg that failed
      setInput(t);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chat">
      <div className="chat-log" ref={scroller}>
        {msgs.length === 0 && (
          <div className="chat-empty">
            Chat to improve the contract — e.g. <em>"tighten the delay-damages clause"</em>, <em>"add a clause requiring 20% local labour"</em>, or <em>"is the retention set up correctly?"</em>. I'll edit the relevant sections and show what changed.
          </div>
        )}
        {msgs.map((m, i) => (
          <div className={`bubble ${m.role}`} key={i}>
            <div className="bubble-text">{m.text}</div>
            {m.applied && m.applied.length > 0 && (
              <div className="bubble-applied">✓ Updated: {m.applied.map((a, j) => <span className="applied-chip" key={j}>{a}</span>)}</div>
            )}
          </div>
        ))}
        {busy && <div className="bubble assistant"><span className="spinner dark-spin" /> thinking…</div>}
      </div>
      <div className="chat-actions">
        <button className="link" onClick={() => send(SUGGEST)} disabled={busy}>✦ Suggest improvements</button>
        {msgs.length > 0 && <button className="link" onClick={() => setMsgs([])} disabled={busy}>Clear</button>}
      </div>
      <div className="chat-input">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
          placeholder={aiReady === false ? "Assistant offline here — works on the deployed site." : "Ask for a change or a review…  (Enter to send)"}
          rows={2}
        />
        <button className="dark" onClick={() => send(input)} disabled={busy || !input.trim()}>{busy ? <span className="spinner" /> : "Send"}</button>
      </div>
      {err && <div className="hint" style={{ color: "#a3402f" }}>{err}</div>}
    </div>
  );
}
